// Registry PASSO → AÇÃO.
//
// NOTA: Dado puro + resolver puro: não importa `.vue`, não toca em `this`, não
// navega. Quem executa é `executar-acao-do-passo.js`. É essa separação que
// deixa o mapa testável isolado e substituível por configuração de backend no
// dia em que a jornada virar configurável.
//
// NOTA: A chave é `step.actionKey`, string vinda do template — nunca índice nem
// título. `STEP_ACTIONS[3]` ou `label === 'Compliance'` mataria a
// configurabilidade que o modelo de dados existe para sustentar.
//
// NOTA: `host` diz ONDE o modal está montado, e não é detalhe: os modais de
// Compliance NÃO estão em `global-modals.vue` — são montados por view (ver
// views/ips/view-ips.vue, views/kyc/view-compliance-kyc.vue, crm-nav-bar.vue).
// Quem emite de fora precisa garantir que o host existe, senão o emit é um
// no-op silencioso: o clique não faz nada e não há erro para investigar.
import { MODAL } from '@constants/Text'
import { SEM_ACAO_DISPONIVEL } from '@modules/client-journey/constants/Text'

export const ACTION_TYPES = { MODAL: 'modal', ROUTE: 'route', NONE: 'none' }

export const HOST = {
  // Montado em src/components/global-modals.vue, disponível em qualquer rota.
  GLOBAL: 'global',
  // Montado pelo shell do próprio módulo (views/nav-bar/).
  CLIENT_JOURNEY: 'client-journey',
}

export const STEP_ACTIONS = {
  // ── Compliance ──────────────────────────────────────────────────────────
  // Payload confirmado em components/ips-client/ips-client.vue:108 e
  // views/overview/view-compliance-overview.vue:855 — `{ item, callBack }`.
  'compliance.ips': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.COMPLIANCE_IPS,
    host: HOST.CLIENT_JOURNEY,
    payload: ({ client, reload }) => ({ item: client, callBack: reload }),
  },
  // Payload confirmado em components/card-kyc/card-kyc.vue:153 —
  // `{ id_cliente_app, callback }`. PLD é KYC no código.
  'compliance.pld': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.COMPLIANCE_KYC_DETAIL,
    host: HOST.CLIENT_JOURNEY,
    payload: ({ client, reload }) => ({
      id_cliente_app: client && (client.id_cliente_app || client.id),
      callback: reload,
    }),
  },
  'compliance.formulario': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.COMPLIANCE_CLIENT_OVERVIEW,
    host: HOST.CLIENT_JOURNEY,
    payload: ({ client }) => ({
      id_cliente_app: client && (client.id_cliente_app || client.id),
    }),
  },
  // Contrato é tela, não modal — o kit de documentos vive numa rota própria.
  'compliance.contrato': {
    type: ACTION_TYPES.ROUTE,
    route: ({ client }) => ({
      name: 'compliance.contracts',
      query: { cliente: client && client.id },
    }),
  },

  // ── Onboarding ──────────────────────────────────────────────────────────
  'onboarding.portabilidade': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.PORTABILIDADE,
    host: HOST.GLOBAL,
    payload: () => ({}),
  },
  // GAP conhecido: não existe fluxo de "abrir conta para ESTE cliente" — o que
  // há é cadastro de conta de investimento, que não aceita cliente
  // pré-selecionado. Navega para a tela de contas do cliente em vez de abrir um
  // modal que ignoraria o contexto.
  'onboarding.abertura-conta': {
    type: ACTION_TYPES.ROUTE,
    route: ({ client }) => ({
      name: 'cliente-perfil',
      params: { id: client && client.id },
    }),
  },
  'onboarding.kickoff': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.CRM_LEAD_EDIT,
    host: HOST.GLOBAL,
    payload: ({ client }) => ({ item: client, tab: 'tarefas' }),
  },

  // ── Relacionamento ──────────────────────────────────────────────────────
  'relacionamento.mensal': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.CRM_LEAD_EDIT,
    host: HOST.GLOBAL,
    payload: ({ client }) => ({ item: client, tab: 'tarefas' }),
  },
  'relacionamento.trimestral': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.CRM_LEAD_EDIT,
    host: HOST.GLOBAL,
    payload: ({ client }) => ({ item: client, tab: 'tarefas' }),
  },

  // ── Entrada ─────────────────────────────────────────────────────────────
  // GAP conhecido: "qualificado" é um booleano do cadastro, não uma tela. O
  // modal de dados do cliente é onde o campo realmente vive.
  'qualificacao.qualificar': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.MEUS_DADOS,
    host: HOST.GLOBAL,
    payload: ({ client }) => ({ item: client, novo: false }),
  },
  'r1.realizar': {
    type: ACTION_TYPES.MODAL,
    modal: MODAL.CRM_LEAD_EDIT,
    host: HOST.GLOBAL,
    payload: ({ client }) => ({ item: client, tab: 'tarefas' }),
  },
  'r2.apresentar-proposta': {
    type: ACTION_TYPES.ROUTE,
    route: () => ({ name: 'simulador.selecionar' }),
  },
}

// NOTA: NONE é estado de primeira classe, não erro: passo que a plataforma não
// automatiza (avaliação anual, por exemplo) é legítimo. A UI o renderiza
// não-clicável com a razão em tooltip, e mantém o atalho para a IA ativo.
export function resolveStepAction(actionKey) {
  if (!actionKey) return { type: ACTION_TYPES.NONE, reason: SEM_ACAO_DISPONIVEL }
  return (
    STEP_ACTIONS[actionKey] || { type: ACTION_TYPES.NONE, reason: SEM_ACAO_DISPONIVEL }
  )
}

export function temAcao(actionKey) {
  return resolveStepAction(actionKey).type !== ACTION_TYPES.NONE
}
